/**
 * Cliente HTTP del panel "Recobros" (roadmap §F2.b).
 *
 * Contratos: ver `src/types/msops.ts`, espejo de
 * `ms-ops/packages/contracts/src/opsApi.ts` (fuente de verdad).
 *
 * ARQUITECTURA (decisión 2026-08-03): el SPA **no** habla con ms-ops por
 * internet — se descartó abrir un ingress público para el microservicio. El
 * admin-api del monorepo hace de **passthrough autenticado**: expone
 * `/api/admin/ops/*` y reenvía internamente a `/v1/*` de ms-ops. Por eso este
 * módulo usa el `request()` de `api/http.ts` igual que el resto de vistas
 * (Impagos, Subscriptions…): misma base `REACT_APP_BACKEND_URL`, misma auth
 * (JWT del admin-api vía `Authorization: Bearer`) y mismo manejo global del
 * 401. Cero superficie pública nueva, cero CORS y cero desajuste de roles.
 *
 * El mapeo de rutas es 1:1 (`/api/admin/ops/X` → `/v1/X`): métodos, query
 * params y bodies son exactamente los del contrato de ms-ops.
 */

import { useSyncExternalStore } from "react";
import { HttpError, request } from "./http";
import type {
  CadenceDetail,
  CadenceListItem,
  CadenceListQuery,
  CadenceWriteResponse,
  CreateNoteBody,
  CreateSuppressionBody,
  DecisionLogItem,
  DecisionLogQuery,
  EpisodeDetail,
  EpisodeListItem,
  EpisodeListQuery,
  EpisodeNote,
  ManualSendBody,
  ManualSendResponse,
  OtpLinkResponse,
  Paginated,
  PauseEpisodeBody,
  PauseEpisodeResponse,
  PaymentLinksResponse,
  ReplaceCadenceStepsBody,
  ResumeEpisodeResponse,
  Suppression,
  SuppressionListQuery,
  CompleteTaskBody,
  DeferTaskBody,
  DeferTaskResponse,
  MarkTaskExternalBody,
  MarkTaskExternalResponse,
  OpenEpisodeResponse,
  ReceivablesOverviewResponse,
  ResolveDisputeBody,
  ResolveDisputeResponse,
  RestoreWriteOffResponse,
  WriteOffListResponse,
  WriteOffLeadResponse,
  DismissTaskBody,
  DismissTaskResponse,
  TaskDetail,
  TaskListItem,
  TaskListQuery,
  TaskSendResponse,
  UpdateCadenceBody,
} from "../types/msops";

/**
 * Prefijo del passthrough en el admin-api. Todo lo que cuelga de aquí se
 * reenvía a `/v1/...` de ms-ops sin transformar.
 */
const OPS_PREFIX = "/api/admin/ops";

type Options = {
  token?: string | null;
};

type RequestOptions = Options & {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
};

/**
 * FEATURE FLAG (sustituye a la antigua `REACT_APP_MSOPS_API_URL`).
 *
 * Ya no hay env var propia del SPA: si `MS_OPS_BASE_URL` no está configurada
 * en el admin-api de ese entorno, el proxy devuelve un **404 limpio** en las
 * rutas `/api/admin/ops/*`. Ese 404 ES la señal de "no disponible aquí".
 *
 * Se modela con un flag de módulo de tres estados en lugar de un probe extra
 * al arrancar: la propia primera llamada del panel hace de sonda, así que no
 * se gasta una request de más ni se retrasa el arranque del SPA.
 *
 *   "unknown"     → todavía no se ha llamado; la navegación se muestra
 *                   (optimista: lo normal es que el entorno lo tenga).
 *   "available"   → alguna llamada respondió algo distinto de 404.
 *   "unavailable" → una llamada devolvió 404 → ocultar la sección.
 *
 * `subscribeMsOpsAvailability` permite que Layout/App se re-rendericen cuando
 * el estado cambia, de modo que el enlace del sidebar desaparece en cuanto se
 * detecta el 404, sin necesidad de recargar la página.
 */
export type MsOpsAvailability = "unknown" | "available" | "unavailable";

let availability: MsOpsAvailability = "unknown";
const listeners = new Set<() => void>();

function setAvailability(next: MsOpsAvailability): void {
  if (availability === next) return;
  availability = next;
  listeners.forEach((listener) => listener());
}

/** Snapshot para `useSyncExternalStore` / lecturas puntuales. */
export function getMsOpsAvailability(): MsOpsAvailability {
  return availability;
}

/** Suscripción para `useSyncExternalStore`. Devuelve el des-suscriptor. */
export function subscribeMsOpsAvailability(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * `false` sólo cuando se ha CONFIRMADO el 404. Mientras siga en "unknown" se
 * asume disponible, para no esconder el panel en el primer render.
 */
export function isMsOpsEnabled(): boolean {
  return availability !== "unavailable";
}

/**
 * Versión reactiva de `isMsOpsEnabled()` para los componentes que deben
 * repintarse cuando se detecta el 404 (el enlace del sidebar). Las RUTAS en
 * cambio se registran siempre: si se desmontaran no habría quien hiciera la
 * llamada que descubre la disponibilidad, y el flag nunca saldría de
 * "unknown". Un acceso directo por URL a un entorno sin ms-ops renderiza la
 * vista, que muestra el aviso de "no disponible en este entorno".
 */
export function useMsOpsEnabled(): boolean {
  return useSyncExternalStore(
    subscribeMsOpsAvailability,
    isMsOpsEnabled,
    // Server snapshot: en SSR/prerender no hay llamadas hechas todavía, así
    // que se muestra igual que en el primer render del cliente.
    () => true,
  );
}

/**
 * `true` cuando el passthrough ha confirmado que ms-ops NO está disponible.
 * Las vistas lo usan para cambiar el mensaje de error por el aviso amable de
 * "no disponible en este entorno" en vez de un "HTTP 404" a secas.
 */
export function useMsOpsUnavailable(): boolean {
  return useSyncExternalStore(
    subscribeMsOpsAvailability,
    () => availability === "unavailable",
    () => false,
  );
}

/**
 * Envoltura sobre `request()` que traduce el 404 del passthrough al flag de
 * disponibilidad. Cualquier otro estado se propaga como `HttpError` para que
 * la vista lo pinte — incluido el 401, que aquí sí es una sesión caducada del
 * admin-api y por tanto debe disparar el handler global de `api/http.ts`.
 */
async function opsRequest<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  try {
    const result = await request<T>(`${OPS_PREFIX}${path}`, opts);
    setAvailability("available");
    return result;
  } catch (err) {
    if (err instanceof HttpError) {
      // 404 del proxy = ms-ops no configurado en este entorno. Cualquier otro
      // código significa que el passthrough existe aunque la llamada falle.
      setAvailability(err.status === 404 ? "unavailable" : "available");
    }
    throw err;
  }
}

/**
 * Serializa una query a `URLSearchParams` omitiendo `undefined`, `null` y
 * cadenas vacías — así un filtro sin usar no viaja como `?q=` y dispara la
 * validación Zod (`min(1)`) del back.
 */
function toParams(query: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

// ---------------------------------------------------------------------------
// Episodios.
// ---------------------------------------------------------------------------

export function listEpisodes(
  query: EpisodeListQuery,
  opts: Options = {},
): Promise<Paginated<EpisodeListItem>> {
  return opsRequest(`/episodes${toParams(query)}`, opts);
}

export function getEpisode(
  id: string,
  opts: Options = {},
): Promise<EpisodeDetail> {
  return opsRequest(`/episodes/${encodeURIComponent(id)}`, opts);
}

export function createEpisodeNote(
  id: string,
  body: CreateNoteBody,
  opts: Options = {},
): Promise<EpisodeNote> {
  return opsRequest(`/episodes/${encodeURIComponent(id)}/notes`, {
    ...opts,
    method: "POST",
    body,
  });
}

export function pauseEpisode(
  id: string,
  body: PauseEpisodeBody,
  opts: Options = {},
): Promise<PauseEpisodeResponse> {
  return opsRequest(`/episodes/${encodeURIComponent(id)}/pause`, {
    ...opts,
    method: "POST",
    body,
  });
}

export function resumeEpisode(
  id: string,
  opts: Options = {},
): Promise<ResumeEpisodeResponse> {
  return opsRequest(`/episodes/${encodeURIComponent(id)}/resume`, {
    ...opts,
    method: "POST",
    body: {},
  });
}

// ---------------------------------------------------------------------------
// Decision log — la ventana al modo shadow.
// ---------------------------------------------------------------------------

export function listDecisionLog(
  query: DecisionLogQuery,
  opts: Options = {},
): Promise<Paginated<DecisionLogItem>> {
  return opsRequest(`/decision-log${toParams(query)}`, opts);
}

// ---------------------------------------------------------------------------
// Supresiones.
// ---------------------------------------------------------------------------

export function listSuppressions(
  query: SuppressionListQuery,
  opts: Options = {},
): Promise<Paginated<Suppression>> {
  return opsRequest(`/suppressions${toParams(query)}`, opts);
}

export function createSuppression(
  body: CreateSuppressionBody,
  opts: Options = {},
): Promise<Suppression> {
  return opsRequest("/suppressions", { ...opts, method: "POST", body });
}

export function deleteSuppression(
  id: string,
  opts: Options = {},
): Promise<void> {
  return opsRequest(`/suppressions/${encodeURIComponent(id)}`, {
    ...opts,
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Cadencias (sólo lectura).
// ---------------------------------------------------------------------------

export function listCadences(
  query: CadenceListQuery,
  opts: Options = {},
): Promise<Paginated<CadenceListItem>> {
  return opsRequest(`/cadences${toParams(query)}`, opts);
}

export function getCadence(
  id: string,
  opts: Options = {},
): Promise<CadenceDetail> {
  return opsRequest(`/cadences/${encodeURIComponent(id)}`, opts);
}

// ---------------------------------------------------------------------------
// F2.d — acciones sobre un episodio.
// ---------------------------------------------------------------------------

/**
 * Envío MANUAL de una plantilla.
 *
 * Ojo con la semántica del 200: si el gate del mercado está OFF, ms-ops
 * responde `simulated: true` con 200 y NO envía nada. No es un error, es el
 * modo shadow aplicado a la acción manual — la vista tiene que distinguirlo
 * del envío real, o Ops creerá que ha escrito al cliente cuando no lo ha hecho.
 *
 * Errores de negocio (llegan como `HttpError` con el cuerpo de ms-ops):
 *   409 supresión vigente / episodio cerrado
 *   422 plantilla no declarada para el mercado (`detail.available` LISTA las
 *       válidas) o episodio sin lead
 *   503 canal no configurado en el entorno
 */
export function sendEpisodeMessage(
  id: string,
  body: ManualSendBody,
  opts: Options = {},
): Promise<ManualSendResponse> {
  return opsRequest(`/episodes/${encodeURIComponent(id)}/send`, {
    ...opts,
    method: "POST",
    body,
  });
}

/**
 * Links de pago (Stripe) de las facturas pendientes del episodio — paridad
 * nº4 del checklist: Ops los copia y los pega.
 *
 * Cada factura trae su `paymentUrl` o su `error`: una que no se pueda resolver
 * NO invalida las demás. 503 si ms-payment no está configurado en el entorno.
 */
export function getEpisodePaymentLinks(
  id: string,
  opts: Options = {},
): Promise<PaymentLinksResponse> {
  return opsRequest(`/episodes/${encodeURIComponent(id)}/payment-link`, opts);
}

/**
 * CREA el link de pago de UN recibo (ms-payment P2.b pieza 1, idempotente:
 * dos clics no crean dos invoices). ms-ops guarda: episodio abierto y recibo
 * PENDIENTE del lead (un disputado se rechaza — va por reemisión). La acción
 * queda auditada con el actor.
 */
export function createEpisodePaymentLink(
  id: string,
  invoiceId: string,
  opts: Options = {},
): Promise<{ invoiceId: string; paymentUrl: string; created: boolean }> {
  return opsRequest(`/episodes/${encodeURIComponent(id)}/payment-link`, {
    ...opts,
    method: "POST",
    body: { invoiceId },
  });
}

/**
 * Link OTP del área de cliente (vía KITT). 503 si KITT no está configurado en
 * el entorno; 422 si el episodio no tiene lead al que asociarlo.
 */
export function getEpisodeOtpLink(
  id: string,
  opts: Options = {},
): Promise<OtpLinkResponse> {
  return opsRequest(`/episodes/${encodeURIComponent(id)}/otp`, opts);
}

// ---------------------------------------------------------------------------
// F2.d — escritura de cadencias (editor del panel).
// ---------------------------------------------------------------------------

/**
 * Metadatos de la cadencia: `label` y/o `paused` (kill-switch de Ops). Al
 * menos uno debe venir — un PUT que no cambia nada sólo generaría una entrada
 * de auditoría falsa. 409 si la cadencia está sembrada por código
 * (`editable: false`).
 */
export function updateCadence(
  id: string,
  body: UpdateCadenceBody,
  opts: Options = {},
): Promise<CadenceWriteResponse> {
  return opsRequest(`/cadences/${encodeURIComponent(id)}`, {
    ...opts,
    method: "PUT",
    body,
  });
}

/**
 * Reemplaza el SET COMPLETO de pasos. `applyScope` gobierna qué pasa con los
 * episodios YA ABIERTOS y la respuesta trae `resyncedEpisodes` — con
 * `new_only` siempre 0, y verlo confirma que no se tocó nada en vuelo.
 *
 * 409 `pasos_en_uso` si se intenta BORRAR un paso al que apuntan episodios
 * abiertos: el `detail` trae una muestra de los episodios y el total.
 */
export function replaceCadenceSteps(
  id: string,
  body: ReplaceCadenceStepsBody,
  opts: Options = {},
): Promise<CadenceWriteResponse> {
  return opsRequest(`/cadences/${encodeURIComponent(id)}/steps`, {
    ...opts,
    method: "PUT",
    body,
  });
}

// ---------------------------------------------------------------------------
// Cola de tareas — el email preparado que una operadora completa y envía.
//
// Es el puente hasta que ms-payment absorba Stripe: el motor decide a quién y
// cuándo y precarga todo lo que sabe; lo que no puede derivar (importes, link
// de pago) viaja DECLARADO en `missingVariables` y lo rellena una persona.
// ---------------------------------------------------------------------------

/**
 * La cola. `status` lo pone el back a `pending` por defecto — una cola existe
 * para vaciarse — así que omitirlo devuelve exactamente lo que queda por hacer.
 * `overdue: true` la convierte en agenda ("¿qué se me ha pasado?").
 */
export function listTasks(
  query: TaskListQuery,
  opts: Options = {},
): Promise<Paginated<TaskListItem>> {
  return opsRequest(`/tasks${toParams(query)}`, opts);
}

/**
 * El detalle ES el formulario: variables ya resueltas (lectura) + las que
 * faltan (edición) + el estado del episodio en el momento de la consulta, que
 * es la señal que evita mandar una resiliación a quien pagó ayer.
 */
export function getTask(id: string, opts: Options = {}): Promise<TaskDetail> {
  return opsRequest(`/tasks/${encodeURIComponent(id)}`, opts);
}

/**
 * Completa la tarea con las variables que faltaban y ENVÍA.
 *
 * Ojo con la semántica del 200, igual que en el envío manual: si el gate del
 * mercado está OFF, ms-ops responde `simulated: true` con 200 y NO envía nada.
 * No es un error, es el modo shadow aplicado a la acción — la vista tiene que
 * distinguirlo del envío real o la operadora creerá que ha escrito al cliente.
 *
 * Errores de negocio (llegan como `HttpError` con el cuerpo de ms-ops):
 *   409 `tarea_no_pendiente` (otra operadora la resolvió: carrera real en una
 *       cola compartida), `supresion_activa`, `episodio_cerrado`,
 *       `episodio_en_disputa`
 *   422 `variables_incompletas` (`detail.missing` LISTA las que faltan),
 *       `episodio_sin_lead`, `canal_no_soportado`
 *   502 `envio_fallido` — la tarea VUELVE a `pending` y se puede reintentar
 *   503 `canal_no_configurado`
 */
export function sendTask(
  id: string,
  body: CompleteTaskBody,
  opts: Options = {},
): Promise<TaskSendResponse> {
  return opsRequest(`/tasks/${encodeURIComponent(id)}/send`, {
    ...opts,
    method: "POST",
    body,
  });
}

/**
 * Cartera de impagados: cada cliente con deuda del ledger, clasificado en su
 * grupo de decisión. Derivada al vuelo en ms-ops (cache 60s) — reconcilia al
 * céntimo con ms-payment por construcción.
 */
export function getReceivablesOverview(
  market: string,
  opts: Options = {},
): Promise<ReceivablesOverviewResponse> {
  return opsRequest(`/receivables/overview?market=${encodeURIComponent(market)}`, opts);
}

/**
 * «Condonar»: write-off de todos los recibos vigentes del lead (CxC-4 de
 * ms-payment; queda en su ledger con actor y motivo). Guardas en servidor:
 * sin episodio abierto y póliza INACTIVE.
 */
export function writeOffLead(
  leadUlid: string,
  market: string,
  body: { motivo: string; condonarPolizaViva?: boolean; cerrarEpisodio?: boolean },
  opts: Options = {},
): Promise<WriteOffLeadResponse> {
  return opsRequest(
    `/receivables/${encodeURIComponent(leadUlid)}/write-off?market=${encodeURIComponent(market)}`,
    { ...opts, method: "POST", body },
  );
}

/** Panel «Condonados»: qué se perdonó, quién, cuándo y por qué. */
export function listWriteOffs(
  market: string,
  opts: Options = {},
): Promise<WriteOffListResponse> {
  return opsRequest(`/receivables/write-offs?market=${encodeURIComponent(market)}`, opts);
}

/**
 * «Restituir»: devuelve el saldo condonado (ajuste positivo en el ledger). No
 * borra la condonación — los dos hechos quedan en la historia del recibo.
 */
export function restoreWriteOff(
  id: string,
  motivo: string,
  opts: Options = {},
): Promise<RestoreWriteOffResponse> {
  return opsRequest(`/receivables/write-offs/${encodeURIComponent(id)}/restore`, {
    ...opts,
    method: "POST",
    body: { motivo },
  });
}

/** «Meter en cadencia»: abre episodio para un lead con deuda sin episodio. */
export function openEpisodeForLead(
  leadUlid: string,
  market: string,
  opts: Options = {},
): Promise<OpenEpisodeResponse> {
  return opsRequest(
    `/receivables/${encodeURIComponent(leadUlid)}/open-episode?market=${encodeURIComponent(market)}`,
    { ...opts, method: "POST" },
  );
}

/**
 * «Aplazar» una tarea: el cliente prometió pagar en otra fecha. La tarea sigue
 * PENDIENTE (a diferencia de descartar, que da el paso por resuelto y hace que
 * esa carta no salga nunca).
 */
export function deferTask(
  id: string,
  body: DeferTaskBody,
  opts: Options = {},
): Promise<DeferTaskResponse> {
  return opsRequest(`/tasks/${encodeURIComponent(id)}/defer`, {
    ...opts,
    method: "POST",
    body,
  });
}

/** Da por resuelta la disputa de un episodio (lo descongela). */
export function resolveDispute(
  episodeId: string,
  body: ResolveDisputeBody,
  opts: Options = {},
): Promise<ResolveDisputeResponse> {
  return opsRequest(`/episodes/${encodeURIComponent(episodeId)}/dispute/resolve`, {
    ...opts,
    method: "POST",
    body,
  });
}

/**
 * «Ajustar fase»: declara cartas ya enviadas FUERA de ms-ops (Gmail, antes del
 * arranque) — registra los pasos como hechos, ancla el reloj a la fecha real y
 * resuelve la tarea. Ver `MarkTaskExternalBody`.
 */
export function markTaskExternal(
  id: string,
  body: MarkTaskExternalBody,
  opts: Options = {},
): Promise<MarkTaskExternalResponse> {
  return opsRequest(`/tasks/${encodeURIComponent(id)}/mark-external`, {
    ...opts,
    method: "POST",
    body,
  });
}

/**
 * «Gastos de recobro»: decide si ESTE recibo repercute al cliente los gastos
 * que ha generado su impago. El IMPORTE no viaja — lo pone ms-payment desde el
 * coste real; aquí solo viaja la decisión. Exonerar (`charge: false`) exige
 * motivo: perdonar gastos es una decisión que alguien revisará.
 */
export function setCollectionFee(
  episodeId: string,
  invoiceId: string,
  body: { charge: boolean; reason?: string },
  opts: Options = {},
): Promise<{ ok: true }> {
  return opsRequest(
    `/episodes/${encodeURIComponent(episodeId)}/invoices/${encodeURIComponent(invoiceId)}/collection-fee`,
    { ...opts, method: "POST", body },
  );
}

/**
 * «Ya pagó por fuera»: el dinero entró por transferencia o en efectivo, no por
 * Stripe. Entra en el libro por la misma vía que un cobro normal, así que el
 * episodio se cierra solo cuando ms-payment publica su evento — esta llamada NO
 * lo cierra. Sin esto, el cliente que ya pagó seguía recibiendo cartas.
 */
export function registerExternalPayment(
  episodeId: string,
  invoiceId: string,
  body: {
    amountCents: number;
    paidAt: string;
    method: "transfer" | "cash" | "other";
    reference?: string;
    note?: string;
  },
  opts: Options = {},
): Promise<{ ok: true }> {
  return opsRequest(
    `/episodes/${encodeURIComponent(episodeId)}/invoices/${encodeURIComponent(invoiceId)}/external-payment`,
    { ...opts, method: "POST", body },
  );
}

/**
 * «Reanudar el cobro»: deshace una pausa de la suscripción. No decide nada
 * sobre la reclamación — solo devuelve la suscripción a su ciclo normal.
 * Idempotente: reanudar algo ya activo no rompe nada.
 */
export function resumeSubscription(
  episodeId: string,
  opts: Options = {},
): Promise<{ subscriptionUuid: string }> {
  return opsRequest(`/episodes/${encodeURIComponent(episodeId)}/resume-subscription`, {
    ...opts,
    method: "POST",
  });
}

/**
 * Descarta la tarea. `motivo` es OBLIGATORIO: lo que se descarta aquí son
 * cosas como "no mandamos la resiliación porque el cliente va a domiciliar", y
 * la decisión tiene consecuencias jurídicas que alguien revisará meses después.
 */
export function dismissTask(
  id: string,
  body: DismissTaskBody,
  opts: Options = {},
): Promise<DismissTaskResponse> {
  return opsRequest(`/tasks/${encodeURIComponent(id)}/dismiss`, {
    ...opts,
    method: "POST",
    body,
  });
}
